/**
 * Minimal external toast store usable from both React and non-React code
 * (e.g. the global 401 handler). Consumed via `useSyncExternalStore`.
 */
export type ToastTone = 'info' | 'success' | 'error';

export interface Toast {
  id: string;
  message: string;
  tone: ToastTone;
}

let toasts: Toast[] = [];
const listeners = new Set<() => void>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot(): Toast[] {
  return toasts;
}

export function dismissToast(id: string): void {
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

export function pushToast(message: string, tone: ToastTone = 'info', ttlMs = 5000): string {
  const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  toasts = [...toasts, { id, message, tone }];
  emit();
  if (ttlMs > 0) {
    timers.set(
      id,
      setTimeout(() => dismissToast(id), ttlMs),
    );
  }
  return id;
}
